import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, UserPlus, Gift } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [media, setMedia] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [mediaId, setMediaId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  useEffect(() => {
    load();
    api.get("/media").then(({ data }) => setMedia(data)).catch(() => {});
  }, []);

  const grant = async (e) => {
    e.preventDefault();
    if (!mediaId) return toast.error("Select a media item");
    setBusy(true);
    try {
      await api.post("/grants", { email, name, media_item_id: mediaId });
      toast.success("Media granted to customer");
      setOpen(false);
      setEmail(""); setName(""); setMediaId("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (users === null) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#ff2a5f]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">Customers</div>
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Customers</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="grant-media-open" className="bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
              <Gift className="mr-1.5 h-4 w-4" /> Grant media to customer
            </Button>
          </DialogTrigger>
          <DialogContent className="border-zinc-800 bg-zinc-900 text-white sm:max-w-md">
            <DialogHeader><DialogTitle className="font-display">Grant media (no order)</DialogTitle></DialogHeader>
            <form onSubmit={grant} className="space-y-4">
              <p className="text-xs text-zinc-500">Add media directly to a customer's library without a purchase. A new customer account is created if the email is unknown.</p>
              <div>
                <Label className="text-zinc-300">Customer email</Label>
                <Input data-testid="grant-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 border-zinc-700 bg-zinc-950 text-white" placeholder="customer@email.com" />
              </div>
              <div>
                <Label className="text-zinc-300">Name (optional)</Label>
                <Input data-testid="grant-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 border-zinc-700 bg-zinc-950 text-white" />
              </div>
              <div>
                <Label className="text-zinc-300">Media item</Label>
                <Select value={mediaId} onValueChange={setMediaId}>
                  <SelectTrigger data-testid="grant-media-select" className="mt-1.5 border-zinc-700 bg-zinc-950 text-white">
                    <SelectValue placeholder="Choose media…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64 border-zinc-800 bg-zinc-900 text-white">
                    {media.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button data-testid="grant-submit" type="submit" disabled={busy} className="w-full bg-[#ff2a5f] text-white hover:bg-[#e01f50]">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Grant media"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-[#0d0d10]">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400">Name</TableHead>
              <TableHead className="text-zinc-400">Email</TableHead>
              <TableHead className="text-zinc-400">Media</TableHead>
              <TableHead className="text-zinc-400">Orders</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow className="border-zinc-800"><TableCell colSpan={4} className="py-12 text-center text-zinc-500">No customers yet.</TableCell></TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="border-zinc-800">
                  <TableCell className="text-white">{u.name}</TableCell>
                  <TableCell className="text-zinc-300">{u.email}</TableCell>
                  <TableCell className="text-zinc-300">{u.media_count}</TableCell>
                  <TableCell className="text-zinc-300">{u.order_count}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
